import { useEffect, useRef, useState } from "react";
import { useCoverPin } from "@/hooks/use-cover-pin";
import reel1 from "@/assets/reels/reel-1.webp";
import reel2 from "@/assets/reels/reel-2.webp";
import reel3 from "@/assets/reels/reel-3.webp";
import reel4 from "@/assets/reels/reel-4.webp";
import fivestarAvatar from "@/assets/reels/fivestar-avatar.png";
import InstagramReelOverlay, {
  type ReelMeta,
} from "@/components/landing/InstagramReelOverlay";
import FilmStripBackdrop from "@/components/landing/FilmStripBackdrop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";

const INSTAGRAM_URL = "https://www.instagram.com/fivestarvids";

const TEAL = "#44C2E3";

// Account-level identity shared by every phone. Per-phone caption + counts
// merged in via PHONES entries below so each reel reads as a different post.
const SHARED_META: Omit<ReelMeta, "caption" | "likes" | "comments" | "shares"> = {
  time: "9:41",
  username: "fivestarvids",
  isVerified: false,
  audio: "Original audio · @fivestarvids",
  avatarSrc: fivestarAvatar,
};

interface PhoneConfig {
  image: string;
  position: { top?: string; bottom?: string; left?: string; right?: string };
  rotation: number;
  enterFrom: "top" | "bottom";
  bobPeriod: number;
  enterDelay: number;
  zIndex: number;
  meta: ReelMeta;
}

// Horizontal row of 4 phones with organic vertical stagger and alternating entry direction.
// Swap image here to change which reel each phone shows.
const PHONES: PhoneConfig[] = [
  {
    image: reel1,
    position: { top: "10%", left: "1%" },
    rotation: -3,
    enterFrom: "top",
    bobPeriod: 1.9,
    enterDelay: 0,
    zIndex: 1,
    meta: {
      ...SHARED_META,
      caption: "OT thriller from the championship round at...",
      likes: "2,847",
      comments: "132",
      shares: "89",
    },
  },
  {
    image: reel2,
    position: { top: "2%", left: "26%" },
    rotation: 4,
    enterFrom: "bottom",
    bobPeriod: 2.3,
    enterDelay: 120,
    zIndex: 2,
    meta: {
      ...SHARED_META,
      caption: "Hat trick highlights from the U16 final game...",
      likes: "1,654",
      comments: "87",
      shares: "56",
    },
  },
  {
    image: reel3,
    position: { top: "12%", left: "51%" },
    rotation: 2,
    enterFrom: "top",
    bobPeriod: 1.7,
    enterDelay: 60,
    zIndex: 1,
    meta: {
      ...SHARED_META,
      caption: "Save of the season from this weekend's tournam...",
      likes: "4,231",
      comments: "215",
      shares: "142",
    },
  },
  {
    image: reel4,
    position: { top: "5%", left: "76%" },
    rotation: -3,
    enterFrom: "bottom",
    bobPeriod: 2.5,
    enterDelay: 180,
    zIndex: 2,
    meta: {
      ...SHARED_META,
      caption: "Faceoff fireworks & a Cinderella story for...",
      likes: "1,209",
      comments: "64",
      shares: "38",
    },
  },
];

const CSS = `
  @keyframes ig-phone-enter-top {
    0% { transform: translateY(-80vh); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes ig-phone-enter-bottom {
    0% { transform: translateY(80vh); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes ig-phone-bob {
    0% { transform: translateY(0); }
    100% { transform: translateY(10px); }
  }

  .ig-phone-anim { will-change: transform, opacity; }

  .ig-phone-anim.initial-top { transform: translateY(-80vh); opacity: 0; }
  .ig-phone-anim.initial-bottom { transform: translateY(80vh); opacity: 0; }

  /* fill-mode: both keeps the 0% keyframe (off-screen) applied during the
     per-phone --enter-delay window so phones don't flash at their resting
     position before the slide-in starts. */
  .ig-phone-anim.active.from-top {
    animation:
      ig-phone-enter-top 1500ms cubic-bezier(0.25, 0.46, 0.45, 0.94) var(--enter-delay, 0ms) both,
      ig-phone-bob var(--bob-period, 2s) ease-in-out calc(1500ms + var(--enter-delay, 0ms)) infinite alternate;
  }

  .ig-phone-anim.active.from-bottom {
    animation:
      ig-phone-enter-bottom 1500ms cubic-bezier(0.25, 0.46, 0.45, 0.94) var(--enter-delay, 0ms) both,
      ig-phone-bob var(--bob-period, 2s) ease-in-out calc(1500ms + var(--enter-delay, 0ms)) infinite alternate;
  }

  /* Pauses the bob (second animation) while leaving the entry (first) at its forwards end-state. */
  .ig-phone-anim.active:hover {
    animation-play-state: running, paused;
  }

  @media (prefers-reduced-motion: reduce) {
    .ig-phone-anim,
    .ig-phone-anim.initial-top,
    .ig-phone-anim.initial-bottom {
      transform: none !important;
      opacity: 1 !important;
    }
    .ig-phone-anim.active.from-top,
    .ig-phone-anim.active.from-bottom {
      animation: none !important;
    }
  }
`;

interface PhoneProps {
  config: PhoneConfig;
  isActive: boolean;
}

const Phone = ({ config, isActive }: PhoneProps) => {
  const {
    image,
    position,
    rotation,
    enterFrom,
    bobPeriod,
    enterDelay,
    zIndex,
  } = config;

  return (
    <div
      className="absolute w-[44%] md:w-[22%]"
      style={{
        ...position,
        transform: `rotate(${rotation}deg)`,
        zIndex,
      }}
    >
      <div
        className={`ig-phone-anim from-${enterFrom} ${
          isActive ? "active" : `initial-${enterFrom}`
        }`}
        style={{
          ["--enter-delay" as string]: `${enterDelay}ms`,
          ["--bob-period" as string]: `${bobPeriod}s`,
        }}
      >
        <div
          className="relative aspect-[9/19.5] rounded-[36px] bg-zinc-900 p-2"
          style={{
            boxShadow:
              "0 25px 50px -12px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.05)",
          }}
        >
          {/* Subtle teal hairline accent on the left bezel */}
          <div
            className="absolute top-[22%] -left-[1px] w-[2px] h-[10%] rounded-r"
            style={{ background: TEAL, opacity: 0.55 }}
          />

          {/* Side button (right) drawn as a tiny sliver for character */}
          <div className="absolute top-[28%] -right-[1px] w-[2px] h-[8%] rounded-l bg-zinc-800" />

          {/* Dynamic island */}
          <div className="absolute top-[2.2%] left-1/2 -translate-x-1/2 w-[28%] h-[3%] rounded-full bg-black z-10" />

          {/* Screen — image fills via object-cover, IG UI overlays on top.
              container-type lets the overlay use cqi units to scale with phone size. */}
          <div
            className="relative w-full h-full rounded-[28px] overflow-hidden bg-black"
            style={{ containerType: "inline-size" }}
          >
            <img
              src={image}
              alt=""
              aria-hidden="true"
              draggable={false}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <InstagramReelOverlay meta={config.meta} isActive={isActive} />

            {/* Whole-screen link to Instagram with a hover "play" affordance. */}
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Watch on Instagram — @fivestarvids"
              className="group absolute inset-0 z-20 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/50"
              />
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="relative w-1/5 scale-75 opacity-0 transition-all duration-300 ease-out group-hover:scale-100 group-hover:opacity-100"
                style={{ filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.5))" }}
              >
                <path
                  d="M8 5 L19 12 L8 19 Z"
                  fill="#fff"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const InstagramCollageSection = () => {
  const collageRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const coverTop = useCoverPin(sectionRef);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const node = collageRef.current;
    if (!node) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          obs.disconnect();
        }
      },
      // Trigger when the collage's top is in the upper half of the viewport.
      { rootMargin: "0px 0px -50% 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{ top: coverTop }}
      className="relative md:sticky bg-black py-14 sm:py-20 md:py-32 overflow-hidden"
    >
      <style>{CSS}</style>

      {/* Full-section curved film-strip backdrop, behind all content */}
      <FilmStripBackdrop />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 text-center">
        <h2 className="text-3xl md:text-5xl font-black italic uppercase leading-[1.05] text-white">
          Fresh from the Sidelines
        </h2>
        <p className="mt-4 md:mt-5 text-lg md:text-xl font-medium text-white">
          Shot Friday, ready to post by Sunday.
        </p>
        <p className="mt-3 md:mt-4 max-w-3xl mx-auto text-base md:text-lg text-zinc-400 font-normal leading-relaxed">
          Fivestar creators are on the sidelines every weekend, capturing high school games, club tournaments, and college events across the country. The reels on our social channels are a sample of what we deliver, edited and ready to post.
        </p>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2.5 rounded-full border-2 border-white px-7 py-3 text-sm md:text-base font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <FontAwesomeIcon icon={faInstagram} className="h-5 w-5" />
          @fivestarvids
        </a>
      </div>

      <div className="relative z-10 mt-16 md:mt-20 px-4 md:px-6">
        <div
          ref={collageRef}
          className="relative w-full max-w-[1400px] mx-auto h-[440px] md:h-[760px]"
        >
          {PHONES.map((config, i) => (
            <Phone key={i} config={config} isActive={active} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InstagramCollageSection;
